import { Box } from "@chakra-ui/react";
import { MobileSideMenu } from "components/MobileSideMenu";
import { stickyBottomBarFirstTriggerStyles } from "components/StickyBottomBar";
import StickyBottomBarTrigger from "components/StickyBottomBar/StickyBottomBarTrigger";
import { getDynamicVariable } from "config/dynamicVariables";
import { DataContextProvider } from "contexts/DataContext";
import { UserContextProvider } from "contexts/HelpAFriend/UserContext";
import { useLanguage, useLocalizedData, useTranslations } from "contexts/LocalizationContext/hooks";
import { ModalContextProvider } from "contexts/ModalContext";
import { StickyBottomBarContextProvider } from "contexts/StickyBottomBarContext";
import { useTopMenu } from "hooks/useMenu";
import CustomHead from "modules/common/components/CustomHead";
import Footer from "modules/common/components/Footer";
import { ViewClaimStatusButton, HeaderNav, MenuItems } from "modules/common/components/HeaderNav";
import { PressLogos } from "modules/common/components/PressLogos";
import replaceAll from "utils/replaceAll";
import StickyBottomBarAdapter from "../../adapters/StickyBottomBar";
import type { ReferralTemplateProps } from "./ReferralTemplate.d";
import { CustomersSay, FAQ, HelpAFriend, Hero, HowItWorks } from "./sections";

const MODAL_CLOSE_EVENT_UA = ["clicked", "Modal", "Close"] as const;
const MODAL_CLOSE_EVENT = { event: "help_friend", label: "close", placement: "modal" } as const;

export const ReferralTemplate = ({
  cmsLanguagesData,
  trustpilotInfo,
  story,
  whiteListDomains,
  serverSideStoryblokPages,
  googleOptimizeExperimentIds,
  nofollowUrls,
  coralCampaign,
  campaignId: storyblokCampaignId,
}: ReferralTemplateProps) => {
  const translations = useTranslations();
  const language = useLanguage();
  const topMenu = useTopMenu(language);
  const { getLocalizedCurrency } = useLocalizedData();

  const commissionValue = (parseInt(coralCampaign?.commission_value, 10) || story.content.commissionValue).toString();

  const serializedStory = replaceAll(story, {
    "{{coral_campaign_commission_value}}": getDynamicVariable(
      "{{coral_campaign_commission_value}}",
      language,
      commissionValue
    ),
  });

  const {
    tag_list: tagList,
    content: {
      heroTitle,
      heroContent,
      heroButtonText,
      heroImgSrc,
      helpAFriendPreheader,
      helpAFriendTitle,
      helpAFriendContent,
      helpAFriendButtonText,
      helpAFriendImgSrc,
      customersSayPreheader,
      customersSayTitle,
      customersSayContent,
      customersSayButtonText,
      customersSayImgSrc,
      howItWorksPreheader,
      howItWorksTitle,
      howItWorksButtonText,
      howItWorksCards,
      faqTitle,
      faqAccordionItems,
      campaignSource,
      stickyBottomBarHeading,
      stickyBottomBarButtonText,
      stickyBottomBarBenefits,
      stickyBottomBarHideBenefits,
      stickyBottomBarVariant,
    },
  } = serializedStory;

  const topBannerText = translations("promo_bar_get_commission", {
    "%commission%": getLocalizedCurrency(Number(commissionValue), "EUR"),
  });
  return (
    <DataContextProvider
      data={{
        trustpilotInfo,
        cmsLanguagesData,
        referralProgramConfig: {
          campaignId: coralCampaign?.id || storyblokCampaignId,
          campaignSource,
          commissionValue,
        },
        whiteListDomains,
        serverSideStoryblokPages,
        googleOptimizeExperimentIds,
        nofollowUrls,
      }}
    >
      <StickyBottomBarContextProvider threshold={0.4}>
        <MobileSideMenu />
        <ModalContextProvider>
          <CustomHead
            title={translations("meta_title")}
            description={translations("meta_description")}
            noindex={tagList.includes("noindex")}
          />
          <HeaderNav
            menuItems={<MenuItems menu={topMenu} />}
            headerButton={<ViewClaimStatusButton />}
            topBannerText={topBannerText}
          />
          <UserContextProvider>
            <ModalContextProvider gaEvent={MODAL_CLOSE_EVENT} uaEvent={MODAL_CLOSE_EVENT_UA}>
              <Box as="main">
                <StickyBottomBarTrigger type="first" styles={stickyBottomBarFirstTriggerStyles} />
                <Hero
                  title={heroTitle}
                  content={heroContent}
                  buttonText={heroButtonText}
                  commissionValue={commissionValue}
                  imgSrc={heroImgSrc}
                />
                <HelpAFriend
                  preheader={helpAFriendPreheader}
                  title={helpAFriendTitle}
                  content={helpAFriendContent}
                  buttonText={helpAFriendButtonText}
                  imgSrc={helpAFriendImgSrc}
                />
                <CustomersSay
                  preheader={customersSayPreheader}
                  title={customersSayTitle}
                  content={customersSayContent}
                  buttonText={customersSayButtonText}
                  imgSrc={customersSayImgSrc}
                />
                <HowItWorks
                  preheader={howItWorksPreheader}
                  title={howItWorksTitle}
                  buttonText={howItWorksButtonText}
                  cards={howItWorksCards}
                />
                <FAQ title={faqTitle} accordionItems={faqAccordionItems} />
                <PressLogos />
              </Box>
            </ModalContextProvider>
          </UserContextProvider>
          <Footer />
        </ModalContextProvider>

        <StickyBottomBarAdapter
          stickyBottomBarVariant={stickyBottomBarVariant}
          stickyBottomBarHeading={stickyBottomBarHeading}
          stickyBottomBarButtonText={stickyBottomBarButtonText}
          stickyBottomBarBenefits={stickyBottomBarBenefits}
          stickyBottomBarHideBenefits={stickyBottomBarHideBenefits}
        />
      </StickyBottomBarContextProvider>
    </DataContextProvider>
  );
};
